/**
 * Deployment Type Configuration
 *
 * Single source of truth for deployment types, categories, and providers.
 * Category is DERIVED from type, never stored in database.
 *
 * ADDING A NEW DEPLOYMENT TYPE:
 * 1. Add to Prisma DeploymentType enum
 * 2. Add entry to DEPLOYMENT_TYPE_CONFIG below
 * 3. Run migration to add enum value
 * That's it. All types auto-derive.
 */

import { DbEnum } from "../../../generated/prisma-enums";
import { type EmbedDeploymentSettings } from "../../../schemas/Deployment";

// =============================================================================
// DEPLOYMENT_TYPE_CONFIG - Single Source of Truth
// =============================================================================

/**
 * Connection type for a deployment
 * - oauth: Requires OAuth flow (Google, Microsoft)
 * - api_key: Requires API key/token entry (Telegram, Twilio, Slack, Airtable)
 * - none: No external connection needed (Embed, API)
 */
export type ConnectionType = "oauth" | "api_key" | "none";

/**
 * Configuration for each deployment type.
 * Add new types here - categories and types derive automatically.
 *
 * @property category - Grouping for UI and shared logic
 * @property provider - The underlying service provider (optional)
 * @property connectionType - How the deployment connects: oauth, api_key, or none
 * @property allowsMultipleDeployments - Can multiple active deployments share one connection?
 *   - false (default): Only ONE active deployment per connection (e.g., email - one inbox = one agent)
 *   - true: Multiple deployments can use the same connection (e.g., Slack - one workspace, multiple bots)
 */
export const DEPLOYMENT_TYPE_CONFIG = {
  // ========== EMAIL ==========
  // One inbox = one agent. Customer sends email, only one agent should reply.
  GMAIL: {
    category: "EMAIL",
    provider: "GOOGLE",
    connectionType: "oauth",
    allowsMultipleDeployments: false
  },
  OUTLOOK: {
    category: "EMAIL",
    provider: "MICROSOFT",
    connectionType: "oauth",
    allowsMultipleDeployments: false
  },
  IMAP: {
    category: "EMAIL",
    provider: null,
    connectionType: "api_key",
    allowsMultipleDeployments: false
  },
  SMTP: {
    category: "EMAIL",
    provider: null,
    connectionType: "api_key",
    allowsMultipleDeployments: false
  },

  // ========== CHAT ==========
  // Slack: Could have multiple bots in same workspace (different channels/purposes)
  SLACK: {
    category: "CHAT",
    provider: "SLACK",
    connectionType: "api_key",
    allowsMultipleDeployments: true
  },
  TELEGRAM: {
    category: "CHAT",
    provider: "TELEGRAM",
    connectionType: "api_key",
    allowsMultipleDeployments: false
  },
  // Teams: One OAuth connection can create multiple bots
  TEAMS: {
    category: "CHAT",
    provider: "MICROSOFT",
    connectionType: "oauth",
    allowsMultipleDeployments: true
  },
  FB_MESSENGER: {
    category: "CHAT",
    provider: "META",
    connectionType: "api_key",
    allowsMultipleDeployments: false
  },
  // Instagram DM: one IG professional account = one agent (direct Meta, no Twilio)
  INSTAGRAM: {
    category: "CHAT",
    provider: "META",
    connectionType: "api_key",
    allowsMultipleDeployments: false
  },

  // ========== PHONE/MESSAGING ==========
  // One phone number = one agent
  WHATSAPP: {
    category: "PHONE",
    provider: "TWILIO",
    connectionType: "api_key",
    allowsMultipleDeployments: false
  },
  TWILIO_SMS: {
    category: "PHONE",
    provider: "TWILIO",
    connectionType: "api_key",
    allowsMultipleDeployments: false
  },

  // ========== VOICE ==========
  // One phone number = one agent
  TWILIO_VOICE: {
    category: "VOICE",
    provider: "TWILIO",
    connectionType: "api_key",
    allowsMultipleDeployments: false
  },

  // ========== DOCUMENT ==========
  // These are typically user-initiated (add-ins), could have multiple
  GOOGLE_SHEETS: {
    category: "DOCUMENT",
    provider: "GOOGLE",
    connectionType: "none", // Session-level OAuth (handled per spreadsheet in Installation tab)
    allowsMultipleDeployments: true
  },
  EXCEL_ADDIN: {
    category: "DOCUMENT",
    provider: "MICROSOFT",
    connectionType: "oauth",
    allowsMultipleDeployments: true
  },
  WORD_ADDIN: {
    category: "DOCUMENT",
    provider: "MICROSOFT",
    connectionType: "oauth",
    allowsMultipleDeployments: true
  },
  POWERPOINT_ADDIN: {
    category: "DOCUMENT",
    provider: "MICROSOFT",
    connectionType: "oauth",
    allowsMultipleDeployments: true
  },
  OUTLOOK_ADDIN: {
    category: "DOCUMENT",
    provider: "MICROSOFT",
    connectionType: "oauth",
    allowsMultipleDeployments: true
  },
  AIRTABLE: {
    category: "DOCUMENT",
    provider: "AIRTABLE",
    connectionType: "api_key",
    allowsMultipleDeployments: true
  },

  // ========== MEETING ==========
  GOOGLE_MEET: {
    category: "MEETING",
    provider: "GOOGLE",
    connectionType: "oauth",
    allowsMultipleDeployments: true
  },
  ZOOM: {
    category: "MEETING",
    provider: "ZOOM",
    connectionType: "oauth",
    allowsMultipleDeployments: true
  },

  // ========== OTHER ==========
  // Embed: Each embed is a unique widget - no external connection
  EMBED: {
    category: "EMBED",
    provider: null,
    connectionType: "none",
    allowsMultipleDeployments: true
  },
  // API: Each API deployment is unique - no external connection
  API: { category: "API", provider: null, connectionType: "none", allowsMultipleDeployments: true }
} as const;

// =============================================================================
// Derived Types (auto-generated from DEPLOYMENT_TYPE_CONFIG)
// =============================================================================

/** All deployment types - derived from Prisma (single source of truth) */
export type DeploymentType = DbEnum.DeploymentType;

/** Deployment categories (EMAIL, CHAT, PHONE, etc.) */
export type DeploymentCategory = (typeof DEPLOYMENT_TYPE_CONFIG)[DeploymentType]["category"];

/** Service providers (GOOGLE, MICROSOFT, SLACK, etc.) */
export type DeploymentProvider = NonNullable<
  (typeof DEPLOYMENT_TYPE_CONFIG)[DeploymentType]["provider"]
>;

/** Extract deployment types by category */
type ExtractByCategory<Category extends DeploymentCategory> = {
  [K in DeploymentType]: (typeof DEPLOYMENT_TYPE_CONFIG)[K]["category"] extends Category
    ? K
    : never;
}[DeploymentType];

/** Email deployment types (GMAIL, OUTLOOK, IMAP, SMTP) */
export type EmailDeploymentType = ExtractByCategory<"EMAIL">;

/** Chat deployment types (SLACK, TELEGRAM, TEAMS, FB_MESSENGER) */
export type ChatDeploymentType = ExtractByCategory<"CHAT">;

/** Phone/SMS deployment types (WHATSAPP, TWILIO_SMS) */
export type PhoneDeploymentType = ExtractByCategory<"PHONE">;

/** Voice call deployment types */
export type VoiceDeploymentType = ExtractByCategory<"VOICE">;

/** Document deployment types (GOOGLE_SHEETS, EXCEL, etc.) */
export type DocumentDeploymentType = ExtractByCategory<"DOCUMENT">;

/** Meeting deployment types (GOOGLE_MEET, ZOOM) */
export type MeetingDeploymentType = ExtractByCategory<"MEETING">;

// =============================================================================
// Runtime Arrays (auto-derived for iteration, dropdowns, etc.)
// =============================================================================

/** All deployment types */
export const ALL_DEPLOYMENT_TYPES = Object.keys(DEPLOYMENT_TYPE_CONFIG) as DeploymentType[];

/** All deployment categories (deduplicated) */
export const ALL_DEPLOYMENT_CATEGORIES = [
  ...new Set(Object.values(DEPLOYMENT_TYPE_CONFIG).map((c) => c.category))
] as DeploymentCategory[];

/** All deployment providers (deduplicated, excluding null) */
export const ALL_DEPLOYMENT_PROVIDERS = [
  ...new Set(
    Object.values(DEPLOYMENT_TYPE_CONFIG)
      .map((c) => c.provider)
      .filter((p): p is NonNullable<typeof p> => p !== null)
  )
] as DeploymentProvider[];

/** Email deployment types */
export const EMAIL_DEPLOYMENT_TYPES = ALL_DEPLOYMENT_TYPES.filter(
  (t) => DEPLOYMENT_TYPE_CONFIG[t].category === "EMAIL"
) as EmailDeploymentType[];

/** Chat deployment types */
export const CHAT_DEPLOYMENT_TYPES = ALL_DEPLOYMENT_TYPES.filter(
  (t) => DEPLOYMENT_TYPE_CONFIG[t].category === "CHAT"
) as ChatDeploymentType[];

/** Phone deployment types */
export const PHONE_DEPLOYMENT_TYPES = ALL_DEPLOYMENT_TYPES.filter(
  (t) => DEPLOYMENT_TYPE_CONFIG[t].category === "PHONE"
) as PhoneDeploymentType[];

/** Voice deployment types */
export const VOICE_DEPLOYMENT_TYPES = ALL_DEPLOYMENT_TYPES.filter(
  (t) => DEPLOYMENT_TYPE_CONFIG[t].category === "VOICE"
) as VoiceDeploymentType[];

/** Document deployment types */
export const DOCUMENT_DEPLOYMENT_TYPES = ALL_DEPLOYMENT_TYPES.filter(
  (t) => DEPLOYMENT_TYPE_CONFIG[t].category === "DOCUMENT"
) as DocumentDeploymentType[];

/** Meeting deployment types */
export const MEETING_DEPLOYMENT_TYPES = ALL_DEPLOYMENT_TYPES.filter(
  (t) => DEPLOYMENT_TYPE_CONFIG[t].category === "MEETING"
) as MeetingDeploymentType[];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the category for a deployment type.
 * Category is DERIVED, never stored in database!
 *
 * @example
 * getDeploymentCategory('GMAIL')  // Returns 'EMAIL'
 * getDeploymentCategory('SLACK')  // Returns 'CHAT'
 */
export function getDeploymentCategory(type: DeploymentType): DeploymentCategory {
  return DEPLOYMENT_TYPE_CONFIG[type].category;
}

/**
 * Get the provider for a deployment type (if any).
 *
 * @example
 * getDeploymentProvider('GMAIL')  // Returns 'GOOGLE'
 * getDeploymentProvider('IMAP')   // Returns null
 */
export function getDeploymentProvider(type: DeploymentType): DeploymentProvider | null {
  return DEPLOYMENT_TYPE_CONFIG[type].provider;
}

/**
 * Get all deployment types for a given category.
 *
 * @example
 * getTypesForCategory('EMAIL')  // Returns ['GMAIL', 'OUTLOOK', ...]
 */
export function getTypesForCategory(category: DeploymentCategory): DeploymentType[] {
  return ALL_DEPLOYMENT_TYPES.filter((t) => DEPLOYMENT_TYPE_CONFIG[t].category === category);
}

/**
 * Get all deployment types for a given provider.
 *
 * @example
 * getTypesForProvider('GOOGLE')  // Returns ['GMAIL', 'GOOGLE_SHEETS', 'GOOGLE_MEET']
 */
export function getTypesForProvider(provider: DeploymentProvider): DeploymentType[] {
  return ALL_DEPLOYMENT_TYPES.filter((t) => DEPLOYMENT_TYPE_CONFIG[t].provider === provider);
}

/**
 * Check if a deployment type is deprecated.
 *
 * @example
 * isDeprecatedType('EMAIL')  // Returns true
 * isDeprecatedType('GMAIL')  // Returns false
 */
export function isDeprecatedType(type: DeploymentType): boolean {
  return (DEPLOYMENT_TYPE_CONFIG[type] as { deprecated?: boolean }).deprecated === true;
}

/**
 * Check if a deployment type allows multiple active deployments per connection.
 *
 * Most types (especially email, phone) only allow ONE active deployment per connection.
 * This prevents multiple agents from responding to the same message.
 *
 * @example
 * allowsMultipleDeployments('GMAIL')  // Returns false - one inbox, one agent
 * allowsMultipleDeployments('SLACK')  // Returns true - can have multiple bots
 */
export function allowsMultipleDeployments(type: DeploymentType): boolean {
  return DEPLOYMENT_TYPE_CONFIG[type].allowsMultipleDeployments;
}

/**
 * Check if a deployment type requires exclusive connection access.
 * Inverse of allowsMultipleDeployments - more readable in some contexts.
 *
 * @example
 * requiresExclusiveConnection('GMAIL')  // Returns true
 * requiresExclusiveConnection('SLACK')  // Returns false
 */
export function requiresExclusiveConnection(type: DeploymentType): boolean {
  return !DEPLOYMENT_TYPE_CONFIG[type].allowsMultipleDeployments;
}

/**
 * Get the connection type for a deployment type.
 *
 * @example
 * getConnectionType('GMAIL')     // Returns 'oauth'
 * getConnectionType('TELEGRAM')  // Returns 'api_key'
 * getConnectionType('EMBED')     // Returns 'none'
 */
export function getConnectionType(type: DeploymentType): ConnectionType {
  return DEPLOYMENT_TYPE_CONFIG[type].connectionType;
}

/**
 * Check if a deployment type uses OAuth.
 */
export function isOAuthDeployment(type: DeploymentType): boolean {
  return DEPLOYMENT_TYPE_CONFIG[type].connectionType === "oauth";
}

/**
 * Check if a deployment type uses API key/token.
 */
export function isApiKeyDeployment(type: DeploymentType): boolean {
  return DEPLOYMENT_TYPE_CONFIG[type].connectionType === "api_key";
}

/**
 * Check if a deployment type needs no external connection.
 */
export function isNoConnectionDeployment(type: DeploymentType): boolean {
  return DEPLOYMENT_TYPE_CONFIG[type].connectionType === "none";
}

// =============================================================================
// Type Guards (Category Checks)
// =============================================================================
// Accept `string` to work with both Prisma's $Enums.DeploymentType and DbEnum.DeploymentType
// They're structurally identical string enums but TypeScript sees them as different types.

/** Type guard: is this an email deployment? */
export function isEmailDeployment(type: string): type is EmailDeploymentType {
  const config = DEPLOYMENT_TYPE_CONFIG[type as DeploymentType];
  return config?.category === "EMAIL";
}

/** Type guard: is this a chat deployment? */
export function isChatDeployment(type: string): type is ChatDeploymentType {
  const config = DEPLOYMENT_TYPE_CONFIG[type as DeploymentType];
  return config?.category === "CHAT";
}

/** Type guard: is this a phone/SMS deployment? */
export function isPhoneDeployment(type: string): type is PhoneDeploymentType {
  const config = DEPLOYMENT_TYPE_CONFIG[type as DeploymentType];
  return config?.category === "PHONE";
}

/** Type guard: is this a voice call deployment? */
export function isVoiceDeployment(type: string): type is VoiceDeploymentType {
  const config = DEPLOYMENT_TYPE_CONFIG[type as DeploymentType];
  return config?.category === "VOICE";
}

/** Type guard: is this a document deployment? */
export function isDocumentDeployment(type: string): type is DocumentDeploymentType {
  const config = DEPLOYMENT_TYPE_CONFIG[type as DeploymentType];
  return config?.category === "DOCUMENT";
}

/** Type guard: is this a meeting deployment? */
export function isMeetingDeployment(type: string): type is MeetingDeploymentType {
  const config = DEPLOYMENT_TYPE_CONFIG[type as DeploymentType];
  return config?.category === "MEETING";
}

/** Type guard: is this an embed deployment? */
export function isEmbedDeployment(type: string): boolean {
  return type === "EMBED";
}

/** Type guard: does a deployment settings object have the embed settings shape? */
export function isEmbedDeploymentSettings(value: unknown): value is EmbedDeploymentSettings {
  return (
    typeof value === "object" &&
    value !== null &&
    "embedSettings" in value &&
    typeof (value as Record<string, unknown>).embedSettings === "object"
  );
}

/** Type guard: is this an API deployment? */
export function isApiDeployment(type: string): boolean {
  return type === "API";
}

// =============================================================================
// Usage Examples
// =============================================================================

/*
// ========== CLEAN CODE (After Refactor) ==========

// 1. Check specific type (no provider checks!)
if (deployment.type === 'GMAIL') {
  // Gmail-specific logic
}

// 2. Get category (derived, never stored!)
const category = getDeploymentCategory(deployment.type);
if (category === 'EMAIL') {
  // Email category logic (shared across Gmail, Outlook, etc.)
}

// 3. Use type guards
if (isEmailDeployment(deployment.type)) {
  // Type is narrowed to EmailDeploymentType
  // TypeScript knows it's GMAIL | OUTLOOK
}

// 4. UI grouping
const emailDeployments = deployments.filter(d =>
  getDeploymentCategory(d.type) === 'EMAIL'
);

// 5. Provider-specific logic
const googleDeployments = deployments.filter(d =>
  getDeploymentProvider(d.type) === 'GOOGLE'
);

// ========== SPAGHETTI CODE (Before Refactor) - DON'T DO THIS ==========

// ❌ if (deployment.type === 'EMAIL' && metadata.provider === 'GMAIL') { ... }
// ❌ if (deployment.type === 'EMAIL' && metadata.provider === 'OUTLOOK') { ... }
// ❌ const provider = deployment.deploymentSettings.provider;

*/
